// /app/frontend/src/components/frugal-v2/components/reports/generatePDF.js
// PDF generation for month summary (160 lines)

export async function generatePDF(report, t, lang, catNames) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;
  
  // Helper to safely get number
  const num = (val) => (typeof val === 'number' ? val : 0);
  
  // Title
  doc.setFontSize(20);
  doc.setTextColor(40, 40, 40);
  doc.text('ZenSaver - ' + (t.title || 'Monthly Report'), pageWidth / 2, yPos, { align: 'center' });
  yPos += 10;
  
  // Month
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  const monthName = new Date().toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { month: 'long', year: 'numeric' });
  doc.text(monthName.charAt(0).toUpperCase() + monthName.slice(1), pageWidth / 2, yPos, { align: 'center' });
  yPos += 15;
  
  // Income & Savings
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text((t.income || 'Income') + ': $' + num(report.income?.total).toFixed(2), 20, yPos);
  doc.text((t.savings || 'Savings') + ': $' + num(report.savings?.total).toFixed(2), 120, yPos);
  yPos += 12;
  
  // Accounts Section (Banks & Cards)
  const accounts = report.accounts || {};
  if (accounts.banks?.length > 0 || accounts.cards?.length > 0) {
    doc.setFontSize(14);
    doc.setTextColor(16, 185, 129);
    doc.text(t.banks || 'Banks & Cards', 20, yPos);
    yPos += 8;
    
    const accountRows = [];
    (accounts.banks || []).forEach(bank => {
      accountRows.push([bank.name, 'Banco', '$' + num(bank.balance).toFixed(2)]);
    });
    (accounts.cards || []).forEach(card => {
      accountRows.push([card.name, 'Tarjeta', '$' + num(card.balance).toFixed(2)]);
    });
    accountRows.push([t.netPosition || 'Net Position', '', '$' + num(accounts.net_position).toFixed(2)]);
    
    autoTable(doc, {
      startY: yPos,
      head: [['Cuenta', 'Tipo', 'Saldo']],
      body: accountRows,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: 20, right: 20 }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
  }
  
  // Expenses Section
  if (report.expenses?.by_category?.length > 0) {
    doc.setFontSize(14);
    doc.setTextColor(16, 185, 129);
    doc.text(t.expenses || 'Expenses', 20, yPos);
    yPos += 8;
    
    const expenseRows = report.expenses.by_category.map(cat => [
      catNames?.[cat.category] || cat.category || 'Unknown',
      '$' + num(cat.amount).toFixed(2),
      num(cat.percentage).toFixed(1) + '%'
    ]);
    expenseRows.push([t.totalExpenses || 'Total', '$' + num(report.expenses.total).toFixed(2), '100%']);
    
    autoTable(doc, {
      startY: yPos,
      head: [[t.category || 'Category', t.amount || 'Amount', t.percentage || '%']],
      body: expenseRows,
      theme: 'striped',
      headStyles: { fillColor: [16, 185, 129] },
      margin: { left: 20, right: 20 }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
  }
  
  // Vital Analysis
  if (report.vital_analysis?.length > 0) {
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setTextColor(14, 165, 233);
    doc.text(t.vitalAnalysis || 'Vital Analysis', 20, yPos);
    yPos += 8;
    
    const vitalRows = report.vital_analysis.map(v => [
      catNames?.[v.category] || v.category || 'Unknown',
      '$' + num(v.budget).toFixed(2),
      '$' + num(v.spent).toFixed(2),
      num(v.consumption_percent).toFixed(1) + '%'
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [[t.category || 'Category', t.budget || 'Budget', t.spent || 'Spent', t.consumption || 'Consumption']],
      body: vitalRows,
      theme: 'striped',
      headStyles: { fillColor: [14, 165, 233] },
      margin: { left: 20, right: 20 }
    });
    
    yPos = doc.lastAutoTable.finalY + 15;
  }
  
  // Budget Analysis
  if (report.budget_analysis?.length > 0) {
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setTextColor(251, 191, 36);
    doc.text(t.budgetAnalysis || 'Budget Analysis', 20, yPos);
    yPos += 8;
    
    const budgetRows = report.budget_analysis.map(b => [
      catNames?.[b.category] || b.category || 'Unknown',
      '$' + num(b.budget).toFixed(2),
      '$' + num(b.spent).toFixed(2),
      num(b.consumption_percent).toFixed(1) + '%'
    ]);
    
    autoTable(doc, {
      startY: yPos,
      head: [[t.category || 'Category', t.budget || 'Budget', t.spent || 'Spent', t.consumption || 'Consumption']],
      body: budgetRows,
      theme: 'striped',
      headStyles: { fillColor: [251, 191, 36] },
      margin: { left: 20, right: 20 }
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }
  
  // Upcoming Scheduled Charges
  const upcoming = report.scheduled_upcoming?.items || [];
  if (upcoming.length > 0) {
    if (yPos > 240) { doc.addPage(); yPos = 20; }
    doc.setFontSize(14);
    doc.setTextColor(251, 146, 60);
    doc.text(t.upcomingCharges || 'Upcoming Scheduled Charges', 20, yPos);
    yPos += 8;
    const upcomingRows = upcoming.map(s => [
      s.name,
      catNames[s.category] || s.category,
      `${String(s.day).padStart(2,'0')}/${String(s.month).padStart(2,'0')}/${s.year}`,
      s.annual ? (t.annual || 'Annual') : '-',
      '$' + num(s.amount).toFixed(2)
    ]);
    upcomingRows.push(['', '', '', t.totalUpcoming || 'Total Upcoming', '$' + num(report.scheduled_upcoming.total).toFixed(2)]);
    autoTable(doc, {
      startY: yPos,
      head: [[t.category || 'Name', 'Cat', t.amount ? 'Date' : 'Date', t.annual || 'Type', t.amount || 'Amount']],
      body: upcomingRows,
      theme: 'striped',
      headStyles: { fillColor: [251, 146, 60] },
      margin: { left: 20, right: 20 }
    });
  }

  // Previous Month Comparison
  const cmp = report.previous_month_comparison;
  if (cmp && cmp.by_category && cmp.by_category.length > 0) {
    if (yPos > 220) { doc.addPage(); yPos = 20; }
    doc.setFontSize(14);
    doc.setTextColor(168, 85, 247);
    const prev = cmp.previous_period || {};
    const title = (t.prevMonthCompare || 'Previous Month Comparison') +
      (prev.year && prev.month ? ` (${String(prev.month).padStart(2,'0')}/${prev.year})` : '');
    doc.text(title, 20, yPos);
    yPos += 8;

    const cmpRows = cmp.by_category.slice(0, 20).map(c => [
      catNames?.[c.category] || c.category || 'Unknown',
      '$' + num(c.current).toFixed(2),
      '$' + num(c.previous).toFixed(2),
      (num(c.diff) >= 0 ? '+' : '') + '$' + num(c.diff).toFixed(2),
      (num(c.percent) >= 0 ? '+' : '') + num(c.percent).toFixed(1) + '%'
    ]);
    const tot = cmp.totals || {};
    cmpRows.push([
      t.totalExpenses || 'Total',
      '$' + num(tot.current).toFixed(2),
      '$' + num(tot.previous).toFixed(2),
      (num(tot.diff) >= 0 ? '+' : '') + '$' + num(tot.diff).toFixed(2),
      (num(tot.percent) >= 0 ? '+' : '') + num(tot.percent).toFixed(1) + '%'
    ]);
    autoTable(doc, {
      startY: yPos,
      head: [[t.category || 'Category', t.thisMonth || 'This', t.prevMonth || 'Prev', 'Δ $', 'Δ %']],
      body: cmpRows,
      theme: 'striped',
      headStyles: { fillColor: [168, 85, 247] },
      margin: { left: 20, right: 20 }
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }

  // Accumulated 12 months
  const acc12 = report.accumulated_12m;
  if (acc12 && acc12.items && acc12.items.length > 0) {
    if (yPos > 220) { doc.addPage(); yPos = 20; }
    doc.setFontSize(14);
    doc.setTextColor(34, 197, 94);
    const title12 = (t.accumulated12m || 'Accumulated last 12 months') +
      (acc12.months_with_data ? ` (${acc12.months_with_data} ${t.months || 'months'})` : '');
    doc.text(title12, 20, yPos);
    yPos += 8;

    const acc12Rows = acc12.items.slice(0, 20).map(c => [
      catNames?.[c.category] || c.category || 'Unknown',
      '$' + num(c.total).toFixed(2),
      '$' + num(c.monthly_avg).toFixed(2),
      num(c.percentage).toFixed(1) + '%'
    ]);
    acc12Rows.push([
      t.grandTotal || 'Grand total',
      '$' + num(acc12.grand_total).toFixed(2),
      '$' + num(acc12.monthly_average_total).toFixed(2),
      '100%'
    ]);
    autoTable(doc, {
      startY: yPos,
      head: [[t.category || 'Category', t.grandTotal || 'Total', t.monthlyAvg || 'Monthly avg', '%']],
      body: acc12Rows,
      theme: 'striped',
      headStyles: { fillColor: [34, 197, 94] },
      margin: { left: 20, right: 20 }
    });
    yPos = doc.lastAutoTable.finalY + 10;
  }
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `ZenSaver - ${new Date().toLocaleDateString()} - Page ${i}/${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }
  
  // Download
  const fileName = `zensaver-report-${new Date().toISOString().slice(0, 7)}.pdf`;
  doc.save(fileName);
}
